<template>
  <div class="min-h-screen flex flex-col">
    <!-- Background Patterns -->
    <BackgroundPatterns />

    <!-- Minimal Header -->
    <header
      class="fixed inset-x-0 top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-200/50"
    >
      <div class="max-w-8xl mx-auto px-4 sm:px-6 py-3">
        <div class="flex items-center justify-between">
          <router-link
            to="/"
            class="flex items-center h-8 hover:scale-105 transition-transform"
          >
            <img
              :src="systemLogo"
              alt="Aberdeen Institute"
              class="h-full w-auto object-contain"
            />
          </router-link>
          <div class="flex items-center gap-2">
            <LanguageSelector />
            <StatusIndicator class="hidden sm:inline-flex" />
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content: Centered Layout -->
    <main
      class="flex-1 flex flex-col justify-center items-center px-4 sm:px-6 py-8 min-h-[calc(100vh-4rem)]"
    >
      <div class="w-full max-w-md sm:max-w-lg">
        <!-- Back Button -->
        <div class="mb-6">
          <router-link
            to="/security"
            class="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeftIcon class="h-4 w-4 mr-2" />
            {{ t("common.back") }}
          </router-link>
        </div>

        <!-- Authentication Check -->
        <div v-if="!isAuthenticated">
          <!-- Not Authenticated Warning -->
          <div class="text-center mb-8">
            <h2
              class="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-3"
            >
              {{ t("passkey.registerTitle") }}
            </h2>
            <p class="text-sm sm:text-base text-gray-500">
              {{ t("passkey.registerSubtitle") }}
            </p>
          </div>

          <!-- Warning Card -->
          <div
            class="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-8 sm:p-10"
          >
            <div class="text-center">
              <div
                class="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 mb-4"
              >
                <ExclamationTriangleIcon class="h-8 w-8 text-amber-600" />
              </div>
              <h3 class="text-lg font-medium text-gray-900 mb-2">
                {{ isZhLocale ? "需要先登录" : "Login Required" }}
              </h3>
              <p class="text-sm text-gray-600 mb-6">
                {{
                  isZhLocale
                    ? "您需要先登录才能注册 Passkey。请先登录您的账户。"
                    : "You need to log in first to register a Passkey. Please log in to your account first."
                }}
              </p>
              <button
                @click="redirectToLogin"
                class="group relative w-full flex justify-center items-center rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 text-base font-semibold text-white shadow-lg hover:from-blue-700 hover:to-blue-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
              >
                <div
                  class="absolute inset-0 rounded-xl bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                ></div>
                <ArrowRightOnRectangleIcon class="mr-2 h-5 w-5 relative z-10" />
                <span class="relative z-10">{{ t("common.login") }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Authenticated: Show Registration Form -->
        <div v-else>
          <!-- Welcome Title -->
          <div class="text-center mb-8">
            <h2
              class="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-3"
            >
              {{ t("passkey.registerTitle") }}
            </h2>
            <p class="text-sm sm:text-base text-gray-500">
              {{ t("passkey.registerSubtitle") }}
            </p>
          </div>

          <!-- Registration Form -->
          <div
            class="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-8 sm:p-10"
          >
            <!-- Step Indicator -->
            <div class="mb-6">
              <div class="flex items-center">
                <div
                  class="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center"
                >
                  <span class="text-indigo-600 font-semibold">1</span>
                </div>
                <div class="ml-4">
                  <h3 class="text-lg font-medium text-gray-900">
                    {{ t("passkey.readyToRegister") }}
                  </h3>
                  <p class="text-sm text-gray-600">
                    {{ t("passkey.tip1") }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Animation Demo -->
            <div class="mb-6">
              <div
                class="relative h-20 w-full bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl overflow-hidden"
              >
                <div class="absolute inset-0 flex items-center justify-center">
                  <div class="passkey-animation">
                    <div class="device-outline"></div>
                    <div class="fingerprint-icon">
                      <FingerPrintIcon class="h-8 w-8 text-indigo-500" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="space-y-3">
              <button
                @click="handleRegister"
                :disabled="loading"
                class="group relative w-full flex justify-center items-center rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-700 px-4 py-3 text-base font-semibold text-white shadow-lg hover:from-indigo-700 hover:to-indigo-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:from-indigo-400 disabled:to-indigo-400 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
              >
                <div
                  class="absolute inset-0 rounded-xl bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                ></div>
                <LoadingSpinner
                  v-if="loading"
                  class="mr-2 h-5 w-5 relative z-10"
                />
                <FingerPrintIcon v-else class="mr-2 h-5 w-5 relative z-10" />
                <span class="relative z-10">{{
                  t("passkey.startRegistration")
                }}</span>
              </button>

              <button
                @click="handleCancel"
                :disabled="loading"
                class="w-full flex justify-center items-center rounded-xl bg-gray-100 px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {{ t("auth.cancel") }}
              </button>
            </div>

            <!-- Tips Section -->
            <div class="mt-8 border-t border-gray-200 pt-6">
              <div class="flex items-center mb-4">
                <InformationCircleIcon class="h-5 w-5 text-indigo-600 mr-2" />
                <h4 class="text-sm font-medium text-gray-900">
                  {{ t("passkey.tips") }}
                </h4>
              </div>
              <ul class="space-y-3">
                <li class="flex items-start">
                  <CheckCircleIcon
                    class="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span class="text-xs text-gray-600">{{
                    t("passkey.tip1")
                  }}</span>
                </li>
                <li class="flex items-start">
                  <ShieldCheckIcon
                    class="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span class="text-xs text-gray-600">{{
                    t("passkey.tip2")
                  }}</span>
                </li>
                <li class="flex items-start">
                  <LockClosedIcon
                    class="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0"
                  />
                  <span class="text-xs text-gray-600">{{
                    t("passkey.tip3")
                  }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import * as Sentry from "@sentry/vue";
import {
  FingerPrintIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ArrowRightOnRectangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  LockClosedIcon,
} from "@heroicons/vue/24/outline";
import { useAuthStore } from "@/stores/auth";
import BackgroundPatterns from "@/components/layout/BackgroundPatterns.vue";
import LanguageSelector from "@/components/common/LanguageSelector.vue";
import StatusIndicator from "@/components/common/StatusIndicator.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { systemConfigService } from "@/services/systemConfigService";
import {
  getPasskeyRegistrationOptions,
  finishPasskeyRegistration,
  base64ToArrayBuffer,
  arrayBufferToBase64,
} from "@/api/auth";
import { jwtService } from "@/services/jwt";
// 添加在import部分后面
interface AuthenticatorAttestationResponse {
  clientDataJSON: ArrayBuffer;
  attestationObject: ArrayBuffer;
}

interface PublicKeyCredential extends Credential {
  rawId: ArrayBuffer;
  response: AuthenticatorAttestationResponse;
}

const router = useRouter();
const { t, locale } = useI18n();
const authStore = useAuthStore();
const loading = ref(false);

// 获取系统logo路径
const systemLogo = computed(() => {
  return systemConfigService.systemLogo;
});

// Computed properties
const isZhLocale = computed(
  () => locale.value === "zh-CN" || locale.value === "zh",
);
const isAuthenticated = computed(() => authStore.isAuthenticated);

// Redirect to login
function redirectToLogin() {
  router.push("/login");
}

// Check authentication on mount
onMounted(() => {
  // If not authenticated, don't proceed
  if (!isAuthenticated.value) {
    console.log("User not authenticated, showing login requirement");
  }
});

// Handle WebAuthn registration
async function handleRegister() {
  // Check if user is authenticated
  if (!isAuthenticated.value) {
    redirectToLogin();
    return;
  }

  // Check WebAuthn support
  if (!isWebAuthnSupported()) {
    if (!isSecureContext()) {
      throw new Error(t("passkey.unsecureContext"));
    }
    throw new Error(t("passkey.browserNotSupported"));
  }

  loading.value = true;
  try {
    // First check if browser supports WebAuthn
    if (!window.PublicKeyCredential) {
      throw new Error(t("passkey.browserNotSupported"));
    }

    // Check if navigator.credentials is available
    if (!navigator.credentials) {
      throw new Error(t("passkey.apiNotAvailable"));
    }

    // 1. Get registration options
    const token = jwtService.getToken();
    if (!token) {
      throw new Error(t("passkey.noToken"));
    }

    const optionsData = await getPasskeyRegistrationOptions();
    console.log(
      "Complete server response:",
      JSON.stringify(optionsData, null, 2),
    );

    // Try to use data from different levels directly
    let options;

    if (optionsData.publicKey?.publicKey) {
      options = optionsData.publicKey.publicKey;
      console.log("Using optionsData.publicKey.publicKey");
    } else if (optionsData.publicKey) {
      options = optionsData.publicKey;
      console.log("Using optionsData.publicKey");
    } else {
      options = optionsData;
      console.log("Using optionsData");
    }

    if (!options) {
      console.error("Unable to get valid options:", optionsData);
      throw new Error(t("passkey.invalidOptions"));
    }

    // Decode challenge
    if (typeof options.challenge === "string" && options.challenge) {
      console.log("Challenge before decoding:", options.challenge);
      try {
        options.challenge = base64ToArrayBuffer(options.challenge);
      } catch (error) {
        console.error("Challenge decoding failed:", error, options.challenge);
        throw new Error(t("passkey.invalidChallenge"));
      }
    } else {
      console.error("Invalid challenge:", options.challenge);
      throw new Error(t("passkey.invalidChallenge"));
    }

    // Decode user.id
    if (
      options.user &&
      typeof options.user.id === "string" &&
      options.user.id
    ) {
      console.log("User ID before decoding:", options.user.id);
      try {
        options.user.id = base64ToArrayBuffer(options.user.id);
      } catch (error) {
        console.error("User ID decoding failed:", error, options.user.id);
        throw new Error(t("passkey.invalidUserId"));
      }
    } else if (options.user) {
      console.error("Invalid user.id:", options.user?.id);
      throw new Error(t("passkey.invalidUserId"));
    }
    console.log("Processed registration options:", options);

    try {
      const credentials = (await navigator.credentials.create({
        publicKey: options,
      })) as PublicKeyCredential;

      if (!credentials) {
        throw new Error(t("passkey.credentialCreationFailed"));
      }

      // 4. Format registration data
      const registerData = {
        id: credentials.id,
        rawId: arrayBufferToBase64(credentials.rawId),
        type: credentials.type,
        response: {
          clientDataJSON: arrayBufferToBase64(
            credentials.response.clientDataJSON,
          ),
          attestationObject: arrayBufferToBase64(
            credentials.response.attestationObject,
          ),
        },
      };

      console.log("Registration data ready:", registerData);

      // 5. Complete registration process
      await finishPasskeyRegistration(registerData);

      // 6. Show success notification and redirect
      notificationService.success("passkey.registerSuccess");
      handleSuccessRedirect();
    } catch (credError: unknown) {
      console.error("Credential creation error:", credError);
      const errorMessage =
        credError instanceof Error ? credError.message : String(credError);
      throw new Error(`${t("passkey.browserError")}: ${errorMessage}`);
    }
  } catch (error: unknown) {
    console.error("Passkey registration error:", error);

    Sentry.captureException(error, {
      tags: {
        component: "PasskeyRegisterView",
        action: "registration",
      },
    });

    const errorMsg =
      error instanceof Error ? error.message : t("passkey.registrationFailed");
    notificationService.error("passkey.registerError", errorMsg);
  } finally {
    loading.value = false;
  }
}

// Handle success redirect
async function handleSuccessRedirect() {
  try {
    await router.push("/security");
  } catch (error) {
    console.error("Redirect error:", error);
    Sentry.captureException(error, {
      tags: {
        component: "PasskeyRegisterView",
        action: "redirectAfterRegister",
      },
      extra: {
        targetPath: "/security",
        currentPath: window.location.pathname,
      },
    });
    window.location.href = "/security";
  }
}

// Check if running in secure context
function isSecureContext() {
  return window.isSecureContext === true;
}

// Check if WebAuthn is supported
function isWebAuthnSupported() {
  return (
    window.PublicKeyCredential !== undefined &&
    navigator.credentials !== undefined &&
    isSecureContext()
  );
}

// Cancel registration
function handleCancel() {
  try {
    router.back();
  } catch (error) {
    console.error("Cancel redirect error:", error);
    Sentry.captureException(error, {
      tags: {
        component: "PasskeyRegisterView",
        action: "cancelRedirect",
      },
    });
    window.location.href = "/security";
  }
}
</script>

<style scoped>
/* Ensure main content is above background */
main {
  position: relative;
  z-index: 10;
}

/* Enhanced focus states */
button:focus-visible {
  outline: 2px solid rgb(99, 102, 241);
  outline-offset: 2px;
}

/* Button hover effects */
button[type="button"]:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px -5px rgba(99, 102, 241, 0.4);
}

button[type="button"]:active:not(:disabled) {
  transform: translateY(0);
}

/* Smooth transitions */
button {
  transition: all 0.2s ease;
}

/* Header backdrop blur enhancement */
header {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  z-index: 50;
}

/* Form card enhanced glass morphism effect */
.bg-white\/80 {
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow:
    0 8px 32px -8px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

/* Improved button states */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

/* Passkey Animation */
.passkey-animation {
  position: relative;
  width: 100px;
  height: 64px;
}

.device-outline {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 2px solid #6366f1;
  border-radius: 8px;
  animation: pulse 2s infinite;
}

.fingerprint-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: glow 2s infinite alternate;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(99, 102, 241, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
  }
}

@keyframes glow {
  from {
    opacity: 0.7;
    transform: translate(-50%, -50%) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.1);
  }
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .max-w-md {
    max-width: 100%;
    padding: 0 1rem;
  }

  .bg-white\/80 {
    margin: 0 0.5rem;
    padding: 2rem 1.5rem !important;
  }

  /* Mobile: Adjust main container */
  main {
    min-height: calc(100vh - 3rem);
    padding: 1rem 1rem 2rem 1rem;
  }

  /* Mobile: Adjust spacing */
  .mb-8 {
    margin-bottom: 1.5rem;
  }
}

/* Subtle animation for the form card */
.bg-white\/80 {
  animation: slideIn 0.6s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
